import "./SortableTableTool.scss";
// REACT; ROUTER
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

// COMPONENTS & FUNCTIONS

// SVG & IMAGES
import * as FaIcons from "react-icons/fa";

// INTERFACES

// NPM INSTALLS

export type SortableTableValue =
  | string
  | number
  | null
  | undefined;

export interface SortableTableColumn<Row> {
  key: string;
  heading: ReactNode;
  render: (row: Row) => ReactNode;
  sortValue: (row: Row) => SortableTableValue;
}

export interface SortableTableContextAction<Row> {
  label: string;
  icon?: ReactNode;
  className?: string;
  action: (row: Row) => void;
}

interface SortableTableToolProps<Row> {
  columns: SortableTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  emptyMessage?: string;
  contextActions?: SortableTableContextAction<Row>[];
}

/*
SORTABLETABLETOOL COMPONENT INFORMATION
  SortableTableTool renders reusable tabular data and handles column sorting.
  Clicking the active heading again reverses its sorting direction.
*/

export default function SortableTableTool<Row>({
  columns,
  rows,
  rowKey,
  emptyMessage = "No saved data.",
  contextActions = [],
}: SortableTableToolProps<Row>) {
  // ::::: STATES :::::
  const [sortKey, setSortKey] = useState<string>();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    "asc"
  );
  const [contextMenu, setContextMenu] = useState<{
    row: Row;
    x: number;
    y: number;
  }>();

  // ::::: USE HOOKS :::::
  useEffect(() => {
    if (!contextMenu) return;

    const closeContextMenu = () => setContextMenu(undefined);
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeContextMenu();
    };

    window.addEventListener("click", closeContextMenu);
    window.addEventListener("contextmenu", closeContextMenu, true);
    window.addEventListener("resize", closeContextMenu);
    window.addEventListener("scroll", closeContextMenu, true);
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("click", closeContextMenu);
      window.removeEventListener(
        "contextmenu",
        closeContextMenu,
        true
      );
      window.removeEventListener("resize", closeContextMenu);
      window.removeEventListener("scroll", closeContextMenu, true);
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [contextMenu]);

  // ::::: CONSTANTS :::::
  const sortedRows = useMemo(() => {
    const selectedColumn = columns.find(
      (column) => column.key === sortKey
    );
    if (!selectedColumn) return rows;

    return [...rows].sort((firstRow, secondRow) => {
      const firstValue = selectedColumn.sortValue(firstRow);
      const secondValue = selectedColumn.sortValue(secondRow);

      if (firstValue == null) return secondValue == null ? 0 : 1;
      if (secondValue == null) return -1;

      const result =
        typeof firstValue === "number" &&
        typeof secondValue === "number"
          ? firstValue - secondValue
          : String(firstValue).localeCompare(String(secondValue), undefined, {
              numeric: true,
              sensitivity: "base",
            });

      return sortDirection === "asc" ? result : -result;
    });
  }, [columns, rows, sortDirection, sortKey]);

  // ::::: HANDLER FUNCTIONS :::::
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((current) =>
        current === "asc" ? "desc" : "asc"
      );
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  const handleContextMenu = (
    event: ReactMouseEvent<HTMLTableRowElement>,
    row: Row
  ) => {
    if (contextActions.length === 0) return;
    event.preventDefault();

    const menuWidth = 210;
    const menuHeight = contextActions.length * 44 + 16;
    setContextMenu({
      row,
      x: Math.max(
        8,
        Math.min(event.clientX, window.innerWidth - menuWidth)
      ),
      y: Math.max(
        8,
        Math.min(event.clientY, window.innerHeight - menuHeight)
      ),
    });
  };

  const handleContextAction = (
    action: SortableTableContextAction<Row>
  ) => {
    if (!contextMenu) return;
    action.action(contextMenu.row);
    setContextMenu(undefined);
  };

  // ::::: JSX :::::
  if (rows.length === 0) {
    return <p className="sortable-table-tool__empty">{emptyMessage}</p>;
  }

  return (
    <>
      <div className="sortable-table-tool">
        <table>
          <thead>
            <tr>
              {columns.map((column) => {
                const isSorted = sortKey === column.key;
                return (
                  <th
                    key={column.key}
                    aria-sort={
                      !isSorted
                        ? "none"
                        : sortDirection === "asc"
                        ? "ascending"
                        : "descending"
                    }
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(column.key)}
                    >
                      <span>{column.heading}</span>
                      <FaIcons.FaChevronUp
                        className={
                          isSorted && sortDirection === "desc"
                            ? "descending"
                            : ""
                        }
                        aria-hidden="true"
                      />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={rowKey(row)}
                onContextMenu={(event) =>
                  handleContextMenu(event, row)
                }
              >
                {columns.map((column) => (
                  <td key={column.key}>{column.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {contextMenu &&
        createPortal(
          <div
            className="sortable-table-tool__context-menu"
            role="menu"
            aria-label="Table row actions"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
            }}
          >
            {contextActions.map((action) => (
              <button
                type="button"
                role="menuitem"
                className={action.className}
                key={action.label}
                onClick={() => handleContextAction(action)}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
